import { Assessment, AssessmentOpen, Changes } from "@appally/collections/User";
import { collections } from "@appally/lib/data/firebase";
import { Sections } from "@appally/lib/data/sections";
import { checkStatus } from "@appally/lib/utils/checkStatus";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { doc, getDoc, setDoc } from "firebase/firestore";

export type Section = keyof Omit<Assessment, "impact">;

export enum AssessmentStatus {
  NOT_STARTED = "Not started",
  STARTED = "Started",
  COMPLETED = "Completed",
}

export type AssessmentState = {
  section: null | Section;
  page: number;
  status: AssessmentStatus;
  answers: Assessment;
  temp: any;
  editMode: boolean;
  open: null | AssessmentOpen;
  updated: boolean;
  changes: Changes;
  changesMade: boolean;
  complete: boolean;
  assessmentComplete: boolean;
};

const initialState: AssessmentState = {
  section: null,
  page: 0,
  status: AssessmentStatus.NOT_STARTED,
  answers: {},
  temp: {},
  editMode: false,
  open: null,
  updated: false,
  changes: {},
  changesMade: false,
  complete: false,
  assessmentComplete: false,
};

const assessmentSlice = createSlice({
  name: "assessment",
  initialState,
  reducers: {
    setSection: (state, action: PayloadAction<Section>) => {
      state.section = action.payload;
    },
    setPage: (state, action: PayloadAction<AssessmentState["page"]>) => {
      state.page = action.payload;
    },
    setStatus: (state, action: PayloadAction<Assessment | undefined>) => {
      state.status = checkStatus(action.payload);
    },
    setAnswers: (state, action: PayloadAction<Assessment | undefined>) => {
      state.answers = action.payload ?? {};
    },
    setComplete: (
      state,
      action: PayloadAction<AssessmentState["complete"]>
    ) => {
      state.complete = action.payload;
    },
    updateAnswers: (state, action: PayloadAction<keyof Assessment>) => {
      let updatedAnswers = {
        ...state.answers[action.payload],
        ...state.temp,
      };

      if (state.complete) {
        updatedAnswers.completed = true;
      }

      state.answers = {
        ...state.answers,
        [action.payload]: updatedAnswers,
      };
    },
    setTemp: (state, action) => {
      state.temp = action.payload;
    },
    setEditMode: (state, action: PayloadAction<boolean>) => {
      state.editMode = action.payload;
    },
    setAssessmentOpen: (state, action: PayloadAction<AssessmentOpen>) => {
      state.open = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateDBAnswers.pending, () => {})
      .addCase(updateDBAnswers.fulfilled, (state) => {
        state.updated = true;
      })
      .addCase(setChanges.pending, () => {
        console.log("updating changes in db");
      })
      .addCase(setChanges.fulfilled, (state) => {
        state.changesMade = true;
      })
      .addCase(setOpen.pending, () => {
        console.log("updating changes in db");
      })
      .addCase(setOpen.fulfilled, (state, action) => {
        if (action.payload?.open) {
          state.open = action.payload.open;
        }
      })
      .addCase(setAssessmentComplete.fulfilled, (state, action) => {
        state.assessmentComplete = true;
      });
  },
});

type UpdateDBAnswersData = {
  id: string;
  section: Section;
  answers: Assessment;
  completed: Section | null;
};

export const updateDBAnswers = createAsyncThunk(
  "user/updateDBAnswers",
  async (data: UpdateDBAnswersData) => {
    let answers = data.answers;
    const completed = data.completed;
    const section = data.section;

    const docRef = doc(collections.users, data.id);
    // const docRef = doc(collections.assessment_users, data.id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      let dbData = docSnap.data();

      if (completed) {
        if (
          dbData.assessment?.[completed] &&
          dbData.assessment?.[completed].completed
        ) {
          console.log("this area is already completed");
        } else {
          dbData.activity = [
            ...dbData.activity,
            {
              title: "Completed <b>" + Sections[completed].title + "</b>",
              timestamp: Date.now(),
            },
          ];
        }
      } else {
        if (
          !dbData.assessment?.[section] ||
          Object.keys(dbData.assessment?.[section]).length === 0
        ) {
          dbData.activity = [
            ...dbData.activity,
            {
              title: "Started <b>" + Sections[section].title + "</b>",
              timestamp: Date.now(),
            },
          ];
        }
      }

      let updatedData = {
        ...dbData,
        assessment: answers,
        last_saved: Date.now(),
      };

      try {
        await setDoc(docRef, updatedData, { merge: true });
        // update localStorage

        let localData = JSON.parse(localStorage.getItem("sita_user") ?? "");

        if (localData) {
          if (!localData.designated_helper) {
            localData.activity = dbData.activity;
          }

          localStorage.setItem(
            "sita_user",
            JSON.stringify({ ...localData, assessment: answers })
          );
        }

        return data;
      } catch (e) {
        console.log("error saving to db", e);
        return false;
      }
    }
  }
);

function deepMerge(oldObj: any, newObj: any): typeof oldObj {
  if (typeof oldObj !== "object" || typeof newObj !== "object") {
    return newObj;
  }

  const mergedObj = { ...oldObj };

  for (const _key in newObj) {
    const key = _key as keyof typeof newObj;
    if (newObj.hasOwnProperty(key)) {
      if (oldObj.hasOwnProperty(key)) {
        if (Array.isArray(oldObj[key]) && Array.isArray(newObj[key])) {
          // If both values are arrays, merge them as arrays
          mergedObj[key] = [...oldObj[key], ...newObj[key]];
        } else {
          // Otherwise, merge recursively
          mergedObj[key] = deepMerge(oldObj[key], newObj[key]);
        }
      } else {
        // Add new property from newObj
        mergedObj[key] = newObj[key];
      }
    }
  }

  return mergedObj;
}

type SetChangesData = {
  id: string;
  changes: Changes;
};

export const setChanges = createAsyncThunk(
  "user/setChanges",
  async (data: SetChangesData) => {
    const docRef = doc(collections.users, data.id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      let dbData = docSnap.data();

      let updatedData = {
        ...dbData,
        changes: dbData.changes
          ? deepMerge(dbData.changes, data.changes)
          : data.changes,
        last_saved: Date.now(),
      };

      try {
        await setDoc(docRef, updatedData);
        return data;
      } catch (e) {
        console.log("error saving to db", e);
        return false;
      }
    }
  }
);

type SetOpenData = {
  id: string;
  open?: string; // Another user ID
};

export const setOpen = createAsyncThunk(
  "user/setOpen",
  async (data: SetOpenData) => {
    const docRef = doc(collections.users, data.id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      let dbData = docSnap.data();

      let updatedData = {
        ...dbData,
      };

      if (!dbData.assessment_open) {
        if (data.open) {
          // Check if data.open is defined
          updatedData.assessment_open = {
            user: data.open,
            timestamp: Date.now(),
          };
        }
      } else {
        // If data.open is not defined, remove assessment_open from updatedData
        const { assessment_open, ...dataWithoutOpen } = updatedData;
        updatedData = { ...dataWithoutOpen };
      }

      try {
        await setDoc(docRef, updatedData);
        return { open: updatedData.assessment_open };
      } catch (e) {
        console.log("error saving to db", e);
        return;
      }
    }
  }
);

export const setAssessmentComplete = createAsyncThunk(
  "user/setAssessmentComplete",
  async (user_id: string) => {
    const docRef = doc(collections.users, user_id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      let dbData = docSnap.data();

      let updatedData = {
        ...dbData,
        assessment_complete: Date.now(),
      };

      try {
        await setDoc(docRef, updatedData);
        console.log("updated");
        return true;
      } catch (e) {
        console.log("error saving to db", e);
        return false;
      }
    }
  }
);

export const {
  setSection,
  setPage,
  setStatus,
  setAnswers,
  setTemp,
  updateAnswers,
  setEditMode,
  setComplete,
  setAssessmentOpen,
} = assessmentSlice.actions;

export default assessmentSlice.reducer;
